import { Box, Image, Spinner, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import QRCode from 'qrcode'

type Props = {
    /** The child's `qrCodeData` — the value the scanner looks the child up by. */
    qrCodeData?: string | null
    label: string
    size?: string
    onClick?: () => void
}

/**
 * Render a child's QR code from `qrCodeData` in the browser.
 *
 * The stored `qrCode` column is unreliable: some rows hold a placeholder string
 * rather than an image, and because `qrCode` and `qrCodeData` can be updated
 * independently they can drift apart. Generating from `qrCodeData` guarantees
 * the picture on screen encodes exactly what `GET /children/by-qr-code` resolves.
 *
 * Only the opaque `qrCodeData` key is encoded — never a name, date of birth or
 * any other detail — so a scan by someone else reveals nothing about the child.
 */
export default function ChildQrCode({
    qrCodeData,
    label,
    size = '96px',
    onClick
}: Props) {
    const { data: src, isLoading } = useQuery({
        queryKey: ['child-qr-image', qrCodeData],
        queryFn: () =>
            QRCode.toDataURL(qrCodeData as string, {
                margin: 1,
                errorCorrectionLevel: 'M'
            }),
        enabled: !!qrCodeData,
        staleTime: Infinity,
        gcTime: Infinity
    })

    if (!qrCodeData) {
        return (
            <Box
                boxSize={size}
                borderWidth="1px"
                borderStyle="dashed"
                borderColor="gray.200"
                borderRadius="md"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={2}
                flexShrink={0}
            >
                <Text fontSize="xs" color="gray.500" textAlign="center">
                    No QR data on record
                </Text>
            </Box>
        )
    }

    if (isLoading || !src) {
        return (
            <Box
                boxSize={size}
                display="flex"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
            >
                <Spinner size="sm" color="gray.400" />
            </Box>
        )
    }

    return (
        <Image
            src={src}
            alt={label}
            boxSize={size}
            borderWidth="1px"
            borderColor="gray.100"
            borderRadius="md"
            cursor={onClick ? 'pointer' : undefined}
            title={onClick ? 'Click to enlarge' : undefined}
            transition="border-color 0.15s"
            _hover={onClick ? { borderColor: 'brand.300' } : undefined}
            onClick={onClick}
            flexShrink={0}
        />
    )
}
